import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { AdminDashboardCompany, AdminDashboardFilters, AdminDashboardSortKey, SortDirection } from '../../../models/adminDashboard';
import { buildAdminDashboardSummary, fetchAdminDashboard, filterAdminDashboardCompanies, logAdminDashboardAudit, sortAdminDashboardCompanies } from '../../../services/admin/dashboardService';
import { MetricsCards } from '../components/MetricsCards';
import { StatusCharts } from '../components/StatusCharts';
import { CompanyTable } from '../components/CompanyTable';

const STATUS_OPTIONS = ['all', 'active', 'suspended'];

/** Companies shown per page in the Companies table. */
const PAGE_SIZE = 10;

export function AdminDashboardPage(): React.ReactElement {
  useDocumentTitle('Admin dashboard · Triplicity Backoffice');
  const { acquireAccessToken, isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState<AdminDashboardCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AdminDashboardFilters>({ status: 'all' });
  const [sortKey, setSortKey] = useState<AdminDashboardSortKey>('merchantCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    let isMounted = true;
    logAdminDashboardAudit('access', { route: '/admin/dashboard', authenticated: isAuthenticated });
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchAdminDashboard(acquireAccessToken, filters);
        if (isMounted) {
          setCompanies(rows);
        }
      } catch (err) {
        if (isMounted) {
          setCompanies([]);
          setError(err instanceof Error ? err.message : 'Failed to load dashboard.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      isMounted = false;
    };
  }, [filters, acquireAccessToken, isAuthenticated]);

  const filteredCompanies = useMemo(() => filterAdminDashboardCompanies(companies, filters), [companies, filters]);
  const summary = useMemo(() => buildAdminDashboardSummary(filteredCompanies), [filteredCompanies]);

  const sortedCompanies = useMemo(
    () => sortAdminDashboardCompanies(filteredCompanies, sortKey, sortDirection),
    [filteredCompanies, sortKey, sortDirection]
  );

  const totalPages = Math.max(1, Math.ceil(sortedCompanies.length / PAGE_SIZE));

  // Keep the current page within range when the result set shrinks (e.g. after filtering).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageCompanies = useMemo(
    () => sortedCompanies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedCompanies, page]
  );

  const rangeStart = sortedCompanies.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, sortedCompanies.length);

  const handleSort = (key: AdminDashboardSortKey): void => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const handleFilterChange = (next: AdminDashboardFilters): void => {
    logAdminDashboardAudit('filter', { filters: next });
    setFilters(next);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Admin dashboard"
        subtitle="Operational view of 3PL companies, merchants, and order activity."
      />
      <div className="bo-admin-toolbar bo-card">
        <label className="bo-field" htmlFor="admin-dashboard-status">
          <span className="bo-field-label">Status</span>
          <select
            id="admin-dashboard-status"
            className="bo-input"
            value={filters.status ?? 'all'}
            onChange={(event) => handleFilterChange({ ...filters, status: event.target.value })}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'all' ? 'All' : option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="bo-field" htmlFor="admin-dashboard-search">
          <span className="bo-field-label">Search</span>
          <input
            id="admin-dashboard-search"
            className="bo-input"
            placeholder="Company name or ID"
            value={filters.search ?? ''}
            onChange={(event) => handleFilterChange({ ...filters, search: event.target.value })}
          />
        </label>
      </div>
      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading dashboard…" />
        </div>
      ) : null}
      {!loading && error ? <ErrorAlert message={error} /> : null}
      {!loading && !error && filteredCompanies.length === 0 ? (
        <EmptyState
          title="No companies available"
          description="Try broadening your filters or check that the dashboard API returns data for the current tenant."
          action={<Link to="/">Back to home</Link>}
        />
      ) : null}
      {!loading && !error && filteredCompanies.length > 0 ? (
        <>
          <MetricsCards summary={summary} />
          <div className="bo-admin-grid">
            <StatusCharts companies={filteredCompanies} />
            <CompanyTable
              companies={pageCompanies}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
          <div className="bo-admin-pagination">
            <span className="bo-muted">
              Showing {rangeStart}–{rangeEnd} of {sortedCompanies.length}
            </span>
            <div className="bo-admin-pagination-controls">
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="bo-muted">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}